/**
 * PHARMAKON — Trésorerie · « Comptes & caisses » : the pure model of C1-C6 (TWP-18).
 *
 * design/ux/02_comptes_caisses_mouvements.md §4-§9; 03_UX_DESIGN P-5 / P-11. The server is the
 * authority on every figure (`GET /comptes`, `/comptes/:id`, `/comptoirs/:id/journee`,
 * `/comptes/apercu`): this module only decides WHAT to draw from what came back — the tree rows
 * (groups, parents with their « Non affecté » line and their virtual caisses), the état badges, the
 * row actions a role may see and why one is disabled, the tiles and the footer lines, the drawer's
 * tabs per type, the form's fields per type and the request bodies. It never adds, subtracts or
 * rounds an amount: money stays the server's decimal strings, only compared (zero / sign) and
 * formatted. No DOM here (components/tresorerie/comptes/*.jsx render it).
 */
import { compareDecimal, toDecimalString } from "@/lib/number-format.js";
import { ETATS_COMPTE } from "@/config/tresorerie-comptes.messages.js";

/**
 * The drawer parameter of any Trésorerie page (03_UX_DESIGN §2.2): `?panneau=compte:<id>`. TWP-20 —
 * one model for every drawer object and the L1 stack: `lib/tresorerie/panneaux.js` (re-exported here
 * for the comptes screens).
 */
export { PARAM_PANNEAU, OBJETS_PANNEAU, lirePanneau, valeurPanneau } from "./panneaux.js";

/** The path tabs of the leaf (UXD-01: tabs live in the path, `?tab=` is rewritten). */
export const ONGLETS_COMPTES = Object.freeze([
  Object.freeze({ value: "comptes", path: "/tresorerie/comptes" }),
  Object.freeze({ value: "sessions", path: "/tresorerie/comptes/sessions" }),
]);
export function ongletDuChemin(pathname) {
  const p = String(pathname ?? "").replace(/\/+$/, "");
  return ONGLETS_COMPTES.find((o) => o.path === p)?.value ?? "comptes";
}

/** Account types (Prisma `TypeEmplacementFonds`) — named once, the référentiel serves the words. */
export const TYPES = Object.freeze({ BANQUE: "BANQUE", ESPECES: "ESPECES", COFFRE: "COFFRE", VIRTUEL: "VIRTUEL", COMPTOIR: "COMPTOIR" });
/** The physical caisses a virtual caisse may live in (Rule V, TD-143 — the server refuses any other). */
export const TYPES_PARENTS_VIRTUEL = Object.freeze([TYPES.ESPECES, TYPES.COFFRE]);
/** The « Nouveau… » menu (F3) in UX 02 §3 order; the caisse physique form also makes a coffre (TD-77). */
export const TYPES_NOUVEAU = Object.freeze([TYPES.BANQUE, TYPES.ESPECES, TYPES.VIRTUEL, TYPES.COMPTOIR]);

// ─── Rights (from the référentiel, never a hand-kept mirror — P-11) ────────────────────────────────

/**
 * What the caller may do here, read from `GET /referentiel` `permissions` (the server's matrix,
 * TD-63 settings applied). An action the role cannot perform is not rendered.
 */
export function droitsComptes(referentiel) {
  const p = referentiel?.permissions ?? {};
  const t = new Set(p.tresorerie_pharmacie ?? []);
  const c = new Set(p.caisse ?? []);
  return Object.freeze({
    lire: t.has("read"),
    gerer: t.has("manage"),
    operer: t.has("operate"),
    configurer: t.has("configure"),
    cloturer: c.has("close"),
    rouvrir: c.has("reopen"),
    ouvrirSession: c.has("open"),
  });
}

// ─── The tree (E-1) ────────────────────────────────────────────────────────────────────────────────

const normaliser = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/** Does an account match the search text (name or code, accents and case ignored)? */
export function correspond(compte, recherche) {
  const q = normaliser(recherche);
  if (!q) return true;
  return normaliser(compte?.libelle).includes(q) || normaliser(compte?.code).includes(q);
}

/**
 * The rows of the tree grid from `GET /comptes` (UX 02 §4.2, §4.4): one non-focusable GROUP row per
 * group that has a visible account (its total on the right), the root accounts under it, a parent
 * physical caisse with children carrying a synthetic « Non affecté » line (its own solde) then its
 * virtual caisses. `recherche` keeps an account that matches or whose child matches; `groupe` keeps
 * one group (a tile's drill). Inactive accounts are already filtered by the server unless asked.
 * @returns {{ lignes: object[], nbComptes: number, filtre: boolean }}
 */
export function lignesArbre(arbre, { recherche = "", groupe = null } = {}) {
  const comptes = arbre?.comptes ?? [];
  const parId = new Map(comptes.map((c) => [c.id, c]));
  const lignes = [];
  let nbComptes = 0;
  for (const g of arbre?.groupes ?? []) {
    if (groupe && g.cle !== groupe) continue;
    const racines = comptes.filter((c) => c.groupe === g.cle && c.niveau === 0);
    const rows = [];
    for (const r of racines) {
      const enfants = (r.enfants ?? []).map((id) => parId.get(id)).filter(Boolean);
      const enfantsVisibles = enfants.filter((e) => correspond(e, recherche));
      if (!correspond(r, recherche) && enfantsVisibles.length === 0) continue;
      const montres = correspond(r, recherche) ? enfants : enfantsVisibles;
      nbComptes += 1 + montres.length;
      const row = { _kind: "data", id: r.id, compte: r };
      if (r.total_physique != null || enfants.length > 0) {
        if (enfants.length > 0) {
          row.enfants = [
            { _kind: "static", id: `non-affecte:${r.id}`, nonAffecte: true, parent: r },
            ...montres.map((e) => ({ _kind: "data", id: e.id, compte: e, parentRow: r })),
          ];
        }
      }
      rows.push(row);
    }
    if (rows.length === 0) continue;
    lignes.push({ _kind: "group", id: `groupe:${g.cle}`, groupe: g }, ...rows);
  }
  return { lignes, nbComptes, filtre: Boolean(normaliser(recherche)) || Boolean(groupe) };
}

/** Accounts in tree order (Ctrl+↑ / Ctrl+↓ in the drawer walk it — AC-C2-03). */
export function ordreComptes(lignes) {
  const ids = [];
  const marcher = (rows) => {
    for (const r of rows ?? []) {
      if (r._kind === "data") ids.push(r.id);
      if (r.enfants) marcher(r.enfants);
    }
  };
  marcher(lignes);
  return ids;
}

/** The previous / next account of `id` in tree order, or null at an end. */
export function compteVoisin(lignes, id, sens) {
  const ids = ordreComptes(lignes);
  const i = ids.indexOf(id);
  if (i < 0) return null;
  return ids[i + sens] ?? null;
}

/** The row kind the DataGrid reads (`rowKind`). */
export const typeDeLigne = (row) => row?._kind ?? "data";

// ─── États, money signs ──────────────────────────────────────────────────────────────────────────

const estNul = (v) => {
  const d = toDecimalString(v);
  return d != null && compareDecimal(d, "0") === 0;
};

/** The état badges of an account row (UX 02 §1.4), in reading order. */
export function etatsCompte(compte) {
  if (!compte) return [];
  const out = [compte.is_actif ? ETATS_COMPTE.ACTIF : ETATS_COMPTE.INACTIF];
  if (compte.etats?.solde_negatif === true) out.push(ETATS_COMPTE.SOLDE_NEGATIF);
  else if (compte.etats?.sous_le_seuil === true || compte.etats?.sous_seuil === true) out.push(ETATS_COMPTE.SOUS_LE_SEUIL);
  return out;
}

/**
 * E-5 — the word of an écart (UX 02 §9.5, RC-32): « Conforme » when it is zero (or the server
 * classed it within the tolerance), « Manque » below zero, « Excédent » above, and the amount
 * without its sign (the word carries it). An unknown écart is null (never « Conforme »).
 * @param {string|null|undefined} valeur decimal string « compté − attendu »
 * @param {string|null} [classement] the server's classement (« CONFORME » within the tolerance)
 * @returns {{ cle: "CONFORME"|"MANQUE"|"EXCEDENT", montant: string } | null}
 */
export function sensEcart(valeur, classement = null) {
  const d = toDecimalString(valeur);
  if (d == null) return null;
  const montant = d.startsWith("-") ? d.slice(1) : d;
  const signe = compareDecimal(d, "0");
  if (signe === 0 || classement === "CONFORME") return { cle: "CONFORME", montant };
  return { cle: signe < 0 ? "MANQUE" : "EXCEDENT", montant };
}

// ─── Row actions (UX 02 §4.5) ──────────────────────────────────────────────────────────────────────

/**
 * The actions of an account row for the caller. TWP-20 — the movement gestures open the one
 * OperationForm on this account: « Nouveau mouvement » (F7) and « Transférer » (Alt+T) for a role
 * that operates (P, M), « Affecter à une caisse virtuelle » (Alt+V) on a physical caisse with an
 * active virtual child. The management actions are the owner's (P-11). A disabled action carries its
 * reason (UX 02 §4.5, TD-177).
 * @param {object} compte a tree row
 * @param {{ gerer: boolean, operer?: boolean }} droits
 * @param {{ enfantsActifs?: number, raisonSolde?: (montant: string) => string, raisons: object, libelles: object }} ctx
 */
export function actionsCompte(compte, droits, { enfantsActifs = 0, raisons, libelles }) {
  if (!compte) return [];
  const out = [];
  if (droits.operer && compte.is_actif) {
    out.push({ id: "mouvement", label: libelles.mouvement, icon: "arrow-left-right", shortcut: "F7" });
    out.push({ id: "transferer", label: libelles.transferer, icon: "arrow-right-left", shortcut: "Alt+T" });
    if (TYPES_PARENTS_VIRTUEL.includes(compte.type) && enfantsActifs > 0) {
      out.push({ id: "affecter", label: libelles.affecter, icon: "folder-input", shortcut: "Alt+V" });
    }
  }
  if (compte.type === TYPES.COMPTOIR) out.push({ id: "journee", label: libelles.journee, icon: "calendar-days" });
  if (!droits.gerer) return out;
  if (TYPES_PARENTS_VIRTUEL.includes(compte.type) && compte.is_actif) {
    out.push({ id: "nouvelle-virtuelle", label: libelles.nouvelleVirtuelle, icon: "folder-plus" });
  }
  out.push({ id: "modifier", label: libelles.modifier, icon: "pencil", shortcut: "Alt+O" });
  if (compte.is_actif) {
    let raison = null;
    if (compte.est_caisse_centrale) raison = raisons.centrale;
    else if (compte.comptoir?.session) raison = raisons.sessionOuverte;
    else if (enfantsActifs > 0) raison = raisons.enfantActif;
    else if (compte.confirme != null && !estNul(compte.confirme)) raison = raisons.soldeNonNul(compte.confirme);
    out.push({ id: "desactiver", label: libelles.desactiver, icon: "eye-off", danger: true, disabled: raison != null, raison });
  } else {
    out.push({ id: "reactiver", label: libelles.reactiver, icon: "eye" });
  }
  return out;
}

/** How many active virtual caisses live in a parent (from the tree payload). */
export function enfantsActifsDe(arbre, compte) {
  const parId = new Map((arbre?.comptes ?? []).map((c) => [c.id, c]));
  return (compte?.enfants ?? []).map((id) => parId.get(id)).filter((e) => e?.is_actif).length;
}

// ─── Tiles and footer (UX 02 §4.2) — server figures only ─────────────────────────────────────────

const groupeDe = (arbre, cle) => (arbre?.groupes ?? []).find((g) => g.cle === cle) ?? null;

/**
 * The four tiles of the liquidity strip, each a door to its group of the tree (drill contract: the
 * tile's value IS the group total the filtered tree shows). `null` figures stay null (RC-67).
 */
export function tuilesComptes(arbre) {
  const pied = arbre?.pied ?? {};
  const banques = groupeDe(arbre, "BANQUES");
  const especes = groupeDe(arbre, "CAISSES_PHYSIQUES");
  const comptoirs = groupeDe(arbre, "COMPTOIRS");
  return [
    { cle: "liquidite", groupe: null, valeur: pied.liquidite_totale ?? null, poste: pied.poste ?? null, ventes: pied.ventes_especes_en_cours ?? null, masque: pied.masque === true },
    { cle: "banques", groupe: "BANQUES", valeur: banques?.total_confirme ?? null, nb: banques?.nb_comptes ?? 0, masque: banques?.masque === true },
    {
      cle: "especes",
      groupe: "CAISSES_PHYSIQUES",
      valeur: especes?.total_confirme ?? null,
      virtuelles: especes?.par_type?.VIRTUEL ?? null,
      coffre: especes?.par_type?.COFFRE ?? null,
      masque: especes?.masque === true,
    },
    {
      cle: "comptoirs",
      groupe: "COMPTOIRS",
      valeur: comptoirs?.total_confirme ?? null,
      sessions: pied.sessions_ouvertes ?? 0,
      ventes: pied.ventes_especes_en_cours ?? null,
      masque: comptoirs?.masque === true,
    },
  ];
}

/** Show a « dont » line only when it says something (a known, non-zero amount). */
export const ligneUtile = (v) => v != null && !estNul(v);

/**
 * The footer of the tree: the whole officine when nothing filters it (posté / ventes en cours /
 * liquidité totale — two labelled parts, never one silent sum, TD-76); a group's own totals when a
 * tile filters it; no money when a free-text search filters it (the client never sums — P-5).
 */
export function piedArbre(arbre, { recherche = "", groupe = null } = {}) {
  if (groupe) {
    const g = groupeDe(arbre, groupe);
    return { mode: "groupe", libelle: g?.libelle ?? "", confirme: g?.total_confirme ?? null, previsionnel: g?.total_previsionnel ?? null, nb: g?.nb_comptes ?? 0, masque: g?.masque === true };
  }
  if (normaliser(recherche)) return { mode: "recherche" };
  const p = arbre?.pied ?? {};
  return {
    mode: "officine",
    poste: p.poste ?? null,
    previsionnel: p.previsionnel ?? null,
    ventes: p.ventes_especes_en_cours ?? null,
    liquidite: p.liquidite_totale ?? null,
    nb: p.nb_comptes ?? 0,
    masque: p.masque === true,
  };
}

// ─── The drawer (UX 02 §5.3-§5.4) ────────────────────────────────────────────────────────────────

/** The tabs of the account drawer per type (lazy: a tab reads its data when first opened, TA-25). */
export function ongletsCompte(compte) {
  if (!compte) return [];
  if (compte.type === TYPES.COMPTOIR) return ["journee", "sessions", "mouvements", "parametres"];
  const out = ["mouvements", "flux", "transferts"];
  if (TYPES_PARENTS_VIRTUEL.includes(compte.type) && (compte.enfants?.length ?? 0) > 0) out.push("virtuelles");
  out.push("parametres");
  return out;
}

/**
 * The three header tiles of the drawer per type (UX 02 §5.3): keys of the fields to show, the server
 * figures they read. BANQUE: solde · disponible · prévisionnel; a parent: total physique · non affecté
 * · prévisionnel; a leaf caisse: solde · prévisionnel · dernier mouvement; a virtual: solde ·
 * prévisionnel · (its parent's line); a comptoir: espèces attendues · posté · ventes en cours.
 */
export function tuilesTiroir(compte) {
  if (!compte) return [];
  if (compte.type === TYPES.COMPTOIR) {
    const c = compte.comptoir ?? {};
    return [
      { cle: "especesAttendues", valeur: c.especes_attendues ?? null, calcule: true },
      { cle: "poste", valeur: c.poste_solde ?? compte.confirme ?? null },
      { cle: "ventesEnCours", valeur: c.ventes_especes_en_cours ?? null },
    ];
  }
  if (compte.type === TYPES.BANQUE) {
    return [
      { cle: "solde", valeur: compte.confirme },
      { cle: "disponible", valeur: compte.disponible },
      { cle: "previsionnel", valeur: compte.previsionnel },
    ];
  }
  if (compte.total_physique != null) {
    return [
      { cle: "totalPhysique", valeur: compte.total_physique, calcule: true },
      { cle: "nonAffecte", valeur: compte.non_affecte },
      { cle: "previsionnel", valeur: compte.total_physique_previsionnel ?? compte.previsionnel },
    ];
  }
  return [
    { cle: "solde", valeur: compte.confirme },
    { cle: "previsionnel", valeur: compte.previsionnel },
    { cle: "dernierMouvement", date: compte.activite?.dernier_mouvement ?? null },
  ];
}

// ─── The forms (C3-C6, UX 02 §6-§7, §9.2) ────────────────────────────────────────────────────────

/** The fields of the form per type (creation; edit drops the opening and the type / code). */
export function champsFormulaire(type, { modification = false } = {}) {
  const base = ["libelle", "code"];
  const ouverture = modification ? [] : ["montant", "provenance"];
  switch (type) {
    case TYPES.BANQUE:
      return ["banque", ...base, "rib", ...ouverture, "seuil", "decouvert", "note"];
    case TYPES.ESPECES:
    case TYPES.COFFRE:
      return [...(modification ? [] : ["nature"]), ...base, "caisseCentrale", ...ouverture, "seuil", "note"];
    case TYPES.VIRTUEL:
      return ["parent", ...base, ...ouverture, "seuil", "note"];
    case TYPES.COMPTOIR:
      return [...base, "poste", ...ouverture, "fondsRetenu", "seuil", "note"];
    default:
      return base;
  }
}

/** UX 02 §6.3 — « Code » proposed from the libellé (first letters + « -001 »), a suggestion only (TD-257). */
export function proposerCode(libelle) {
  const mots = normaliser(libelle)
    .replace(/[^a-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter((m) => m && !["de", "du", "des", "la", "le", "les", "l", "d", "et"].includes(m));
  if (mots.length === 0) return "";
  const lettres = mots.length === 1 ? mots[0].slice(0, 3) : mots.slice(0, 3).map((m) => m[0]).join("");
  return `${lettres.toUpperCase()}-001`;
}

/**
 * One typed change on the account form. A new account's code follows its libellé (the proposal)
 * until the user types a code of their own; an edit never re-proposes; a comptoir's code is the
 * poste's (served), never derived from the libellé.
 */
export function appliquerSaisie(valeurs, patch, { modification = false } = {}) {
  const n = { ...valeurs, ...patch };
  if ("code" in patch) n.codePropose = false;
  else if ("libelle" in patch && !modification && valeurs.codePropose !== false && valeurs.type !== TYPES.COMPTOIR) {
    n.code = proposerCode(patch.libelle);
    n.codePropose = n.code ? true : null;
  }
  return n;
}

/** The default provenance of an opening per type (TD-180, served by the référentiel). */
export function provenanceParDefaut(type, referentiel) {
  const t = (referentiel?.types_emplacement ?? []).find((x) => x.cle === type);
  return t?.provenance_ouverture ?? (type === TYPES.VIRTUEL || type === TYPES.COMPTOIR ? "TRANSFERT" : "SOLDE_INITIAL");
}

/** The empty form of a new account (the parent pre-filled when opened from a parent). */
export function valeursInitiales(type, { parentId = null, centraleId = null, proposition = null, referentiel = null } = {}) {
  return {
    type,
    libelle: proposition?.libelle ?? "",
    code: proposition?.code ?? "",
    // true: the code shown is the proposal; null: nothing typed yet (the libellé proposes one);
    // false: the user typed the code (never overwritten).
    codePropose: proposition?.code ? true : null,
    banque: "",
    rib: "",
    seuil: null,
    decouvert: null,
    note: "",
    is_defaut: false,
    parent_id: type === TYPES.VIRTUEL ? (parentId ?? centraleId ?? null) : null,
    post_licence_id: null,
    montant: null,
    mode: provenanceParDefaut(type, referentiel),
    source_emplacement_id: null,
  };
}

/** The form of an existing account (edit mode): its configuration only. */
export function valeursDuCompte(compte) {
  return {
    type: compte.type,
    libelle: compte.libelle ?? "",
    code: compte.code ?? "",
    codePropose: false,
    banque: compte.banque ?? "",
    rib: compte.rib ?? "",
    seuil: compte.seuil_alerte_solde ?? null,
    decouvert: compte.decouvert_autorise ?? null,
    note: compte.notes ?? "",
    is_defaut: compte.is_defaut === true,
    parent_id: compte.parent_id ?? null,
    post_licence_id: compte.comptoir?.poste?.id ?? null,
    montant: null,
    mode: null,
    source_emplacement_id: null,
  };
}

const vide = (v) => v == null || String(v).trim() === "";
const ribNormalise = (rib) => String(rib ?? "").replace(/\s+/g, "");

/**
 * The client mirror of the required fields (the server stays authoritative and its sentences are
 * shown verbatim): field → message. `messages` = FORMULAIRE_COMPTE.erreurs + .champs.
 */
export function erreursFormulaire(v, { modification = false, messages, banqueRequise = false } = {}) {
  const e = {};
  const C = messages.champs;
  const E = messages.erreurs;
  if (vide(v.libelle)) e.libelle = E.requis(v.type === TYPES.BANQUE ? C.libelleBanque : C.libelle);
  if (!modification && vide(v.code)) e.code = E.requis(C.code);
  if (v.type === TYPES.BANQUE && banqueRequise) {
    if (vide(v.banque)) e.banque = E.banque;
    if (vide(ribNormalise(v.rib))) e.rib = E.rib;
  }
  if (v.type === TYPES.VIRTUEL && !modification && vide(v.parent_id)) e.parent_id = E.parent;
  if (!modification && v.montant != null && !estNul(v.montant) && v.mode === "TRANSFERT" && v.type !== TYPES.VIRTUEL && v.type !== TYPES.COMPTOIR && vide(v.source_emplacement_id)) {
    e.source_emplacement_id = E.source;
  }
  return e;
}

/** A form value as the API wants it: trimmed text, null for empty, decimal strings untouched. */
const texteOuNull = (v) => (vide(v) ? null : String(v).trim());

/** The body of `POST /comptes` (the type's fields only — the server refuses the others). */
export function corpsCreation(v) {
  const corps = { type: v.type, code: String(v.code ?? "").trim().toUpperCase(), libelle: String(v.libelle ?? "").trim() };
  const notes = texteOuNull(v.note);
  if (notes) corps.notes = notes;
  if (v.seuil != null) corps.seuil_alerte_solde = v.seuil;
  if (v.type === TYPES.BANQUE) {
    corps.banque = texteOuNull(v.banque);
    corps.rib = texteOuNull(ribNormalise(v.rib));
    if (v.decouvert != null) corps.decouvert_autorise = v.decouvert;
  }
  if (v.type === TYPES.ESPECES && v.is_defaut === true) corps.is_defaut = true;
  if (v.type === TYPES.VIRTUEL) corps.parent_id = v.parent_id ?? null;
  if (v.type === TYPES.COMPTOIR && v.post_licence_id) corps.post_licence_id = v.post_licence_id;
  if (v.montant != null && !estNul(v.montant)) {
    corps.ouverture = { montant: v.montant, mode: v.mode };
    if (v.mode === "TRANSFERT" && v.source_emplacement_id) corps.ouverture.source_emplacement_id = v.source_emplacement_id;
  }
  return corps;
}

/** The body of `PUT /comptes/:id`: only the configuration fields that changed (If-Match carries the version). */
export function corpsModification(v, compte) {
  const avant = valeursDuCompte(compte);
  const corps = {};
  const pose = (cle, champ, valeur) => {
    if (valeur !== avant[champ] && !(vide(valeur) && vide(avant[champ]))) corps[cle] = valeur;
  };
  pose("libelle", "libelle", String(v.libelle ?? "").trim());
  pose("notes", "note", texteOuNull(v.note));
  pose("seuil_alerte_solde", "seuil", v.seuil ?? null);
  if (v.type === TYPES.BANQUE) {
    pose("banque", "banque", texteOuNull(v.banque));
    if (ribNormalise(v.rib) !== ribNormalise(avant.rib)) corps.rib = texteOuNull(ribNormalise(v.rib));
    pose("decouvert_autorise", "decouvert", v.decouvert ?? null);
  }
  if (v.type === TYPES.ESPECES && v.is_defaut !== avant.is_defaut) corps.is_defaut = v.is_defaut === true;
  if (v.type === TYPES.COMPTOIR && (v.post_licence_id ?? null) !== (avant.post_licence_id ?? null)) corps.post_licence_id = v.post_licence_id ?? null;
  if (v.type === TYPES.VIRTUEL && (v.parent_id ?? null) !== (avant.parent_id ?? null)) corps.parent_id = v.parent_id ?? null;
  return corps;
}

/** The body of `POST /comptes/apercu` for the form being typed (null when there is nothing to preview). */
export function corpsApercu(v) {
  if (v.montant == null || estNul(v.montant)) return null;
  if (v.mode !== "TRANSFERT" && v.type !== TYPES.VIRTUEL) return null;
  if (v.type === TYPES.VIRTUEL && !v.parent_id) return null;
  return {
    type: v.type,
    ...(v.type === TYPES.VIRTUEL ? { parent_id: v.parent_id } : {}),
    ouverture: { montant: v.montant, mode: v.mode, ...(v.mode === "TRANSFERT" && v.source_emplacement_id ? { source_emplacement_id: v.source_emplacement_id } : {}) },
  };
}

/** The accounts a TRANSFERT opening may come from (active, not the account being made, never a virtual's foreign parent). */
export function sourcesOuverture(arbre, { type, parentId = null } = {}) {
  return (arbre?.comptes ?? []).filter((c) => c.is_actif && (type !== TYPES.VIRTUEL || c.id === parentId));
}

/** The physical caisses a virtual caisse may live in (active ESPÈCES / COFFRE roots, Rule V). */
export function parentsPossibles(arbre) {
  return (arbre?.comptes ?? []).filter((c) => c.is_actif && c.niveau === 0 && TYPES_PARENTS_VIRTUEL.includes(c.type));
}

// ─── Sessions (UX 02 §9.5) ───────────────────────────────────────────────────────────────────────

/** The filter keys of the sessions tab — the `GET /caisse` parameters themselves (URL-synced by useFilters). */
export const FILTRES_SESSIONS = Object.freeze({
  COMPTOIR: "emplacement_fonds_id",
  CAISSIER: "caissier_id",
  STATUT: "statut",
  AVEC_ECART: "avec_ecart",
  DU: "date_debut",
  AU: "date_fin",
});
export const PARAM_PAGE_SESSIONS = "page";

/**
 * The query of `GET /caisse` from the tab's filters (useFilters `queryParams`) and page: server-paged,
 * with the totals of the WHOLE filtered set (`totaux=1`, TD-252). A fixed comptoir (the comptoir
 * drawer's « Sessions » tab) wins over the filter.
 */
export function requeteSessions(filtres, { page = 1, limite, comptoirId = null } = {}) {
  const f = filtres ?? {};
  const oui = f[FILTRES_SESSIONS.AVEC_ECART] === true || f[FILTRES_SESSIONS.AVEC_ECART] === "true";
  const comptoir = comptoirId ?? f[FILTRES_SESSIONS.COMPTOIR] ?? null;
  return {
    page: Math.max(1, Number.parseInt(String(page), 10) || 1),
    limit: limite,
    totaux: "1",
    ...(comptoir ? { emplacement_fonds_id: comptoir } : {}),
    ...(f[FILTRES_SESSIONS.CAISSIER] ? { caissier_id: f[FILTRES_SESSIONS.CAISSIER] } : {}),
    ...(f[FILTRES_SESSIONS.STATUT] ? { statut: f[FILTRES_SESSIONS.STATUT] } : {}),
    ...(oui ? { avec_ecart: "true" } : {}),
    ...(f[FILTRES_SESSIONS.DU] ? { date_debut: f[FILTRES_SESSIONS.DU] } : {}),
    ...(f[FILTRES_SESSIONS.AU] ? { date_fin: f[FILTRES_SESSIONS.AU] } : {}),
  };
}

/** The figures of a session row, from the one formula's `especes` block (decimal strings). */
export function chiffresSession(s) {
  const e = s?.especes ?? null;
  return {
    attendues: e?.attendues ?? null,
    comptees: s?.statut === "CLOTUREE" ? (e?.compte ?? (s?.fonds_cloture != null ? toDecimalString(s.fonds_cloture) : null)) : null,
    ecart: s?.statut === "CLOTUREE" ? (e?.ecart ?? null) : null,
    recalcule: e?.recalcule === true,
    // TD-20 — the server withholds the expected cash of an open session from a blind-count reader.
    masqueAveugle: e?.masque === true,
  };
}

/** The actions of a session row for the caller (old /caisses parity). */
export function actionsSession(s, droits, { moi = null } = {}) {
  if (!s) return [];
  const out = [{ id: "ouvrir", icon: "panel-right-open" }];
  const ouverte = s.statut === "OUVERTE";
  const sienne = moi && (s.caissier?.id ?? s.caissier_id) === moi;
  if (ouverte && droits.cloturer && (droits.gerer || droits.operer || sienne)) out.push({ id: "cloturer", icon: "lock" });
  if (ouverte && droits.operer) out.push({ id: "ajuster", icon: "sliders-horizontal" });
  if (!ouverte && droits.rouvrir) out.push({ id: "rouvrir", icon: "rotate-ccw" });
  out.push({ id: ouverte ? "rapportX" : "rapportZ", icon: "printer" });
  if (s.emplacement_fonds?.id) out.push({ id: "journee", icon: "calendar-days" });
  return out;
}
